import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import dashengBgm from 'asset/music/dasheng.mp3';
import { WK_APP_BACKGROUND, WK_APP_FOREGROUND } from 'utils/appAudioEvents';

const PET_BGM_PATHS = ['/pet', '/foodlist', '/petrecord', '/daily-task'];

let sharedAudio = null;

function getSharedAudio() {
  if (!sharedAudio) {
    const audio = new Audio(dashengBgm);
    audio.loop = true;
    audio.volume = 0.5;
    sharedAudio = audio;
  }
  return sharedAudio;
}

function releaseSharedAudio() {
  if (sharedAudio) {
    sharedAudio.pause();
    sharedAudio.src = '';
    sharedAudio = null;
  }
}

/**
 * Pet 区域背景音乐：在 pet、食物商城、技能介绍(同 pet)、大圣图鉴、每日任务 下共用一个音频实例循环播放，切换不中断。
 * 挂载在 App 层，根据路由自动 play/pause；离开时释放实例以节省内存。
 */
const PetBgm = () => {
  const location = useLocation();
  const pathname = location.pathname || '';
  const pathLower = pathname.toLowerCase();
  const isInPetArea = PET_BGM_PATHS.some((p) => pathLower === p || pathLower.startsWith(p + '/'));
  const wasPlayingBeforeBackgroundRef = useRef(false);

  useEffect(() => {
    const pathInPetArea = (path) => {
      const pl = (path || '').toLowerCase();
      return PET_BGM_PATHS.some((p) => pl === p || pl.startsWith(p + '/'));
    };
    const onBg = () => {
      if (!pathInPetArea(window.location.pathname)) {
        wasPlayingBeforeBackgroundRef.current = false;
        return;
      }
      const audio = getSharedAudio();
      if (audio && !audio.paused) {
        wasPlayingBeforeBackgroundRef.current = true;
        audio.pause();
      } else {
        wasPlayingBeforeBackgroundRef.current = false;
      }
    };
    const onFg = () => {
      if (!wasPlayingBeforeBackgroundRef.current) return;
      wasPlayingBeforeBackgroundRef.current = false;
      if (!pathInPetArea(window.location.pathname)) return;
      getSharedAudio().play().catch(() => {});
    };
    window.addEventListener(WK_APP_BACKGROUND, onBg);
    window.addEventListener(WK_APP_FOREGROUND, onFg);
    return () => {
      window.removeEventListener(WK_APP_BACKGROUND, onBg);
      window.removeEventListener(WK_APP_FOREGROUND, onFg);
    };
  }, []);

  useEffect(() => {
    if (!isInPetArea) {
      releaseSharedAudio();
      return;
    }
    const audio = getSharedAudio();
    audio.play().catch(() => {});
  }, [isInPetArea]);

  useEffect(() => {
    const onPause = () => {
      const a = getSharedAudio();
      if (a && !a.paused) a.pause();
    };
    const onResume = () => {
      if (isInPetArea) getSharedAudio().play().catch(() => {});
    };
    window.addEventListener('pet-bgm-pause', onPause);
    window.addEventListener('pet-bgm-resume', onResume);
    return () => {
      window.removeEventListener('pet-bgm-pause', onPause);
      window.removeEventListener('pet-bgm-resume', onResume);
    };
  }, [isInPetArea]);

  useEffect(() => {
    if (!isInPetArea) return;
    const audio = getSharedAudio();
    const tryPlay = () => {
      if (audio.paused) audio.play().catch(() => {});
    };
    const opts = { passive: true, once: true };
    document.addEventListener('touchstart', tryPlay, opts);
    document.addEventListener('click', tryPlay, opts);
    return () => {
      document.removeEventListener('touchstart', tryPlay, opts);
      document.removeEventListener('click', tryPlay, opts);
    };
  }, [isInPetArea]);

  return null;
};

export default PetBgm;
